import {
  Button,
  Clickable,
  Tabs,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import { debounce } from 'lodash';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { User } from '../../Lib/Api/vendor';
import Page from '../Page';
import Header from '../Header';
import EmptyList from './EmptyList';
import AddItemsModal from './AddItems/AddItemsModal';
import ItemCardList from './ItemCardList';
import { useParamFilter } from '../../Lib/Hooks/Filters/useParamFilter';
import {
  useCountVendorCatalogItems,
  useGetVendorItems,
  ItemFilters,
  possibleParams,
  useGetRemainingItemToUpload,
} from '../../Lib/Hooks/ItemService';
import { VPStages } from './enums/stage.enums';
import { CategoryClassFilter } from './filters/CategoryClassFilter';

type SellToUsProps = {
  user: User;
};

// TODO: Remove this eslint-disable-next-line when the component is implemented
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SellToUs(_: SellToUsProps) {
  const { id: userId } = useParams<{
    id: string;
  }>();

  const [isAddItemsOpened, setIsAddItemsOpened] = useState(false);

  const { paramFilters, setParamFilter } = useParamFilter(possibleParams);

  const { items, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetVendorItems(userId, { paramFilters }, { staleTime: 30 * 1000 });

  const { data: count } = useCountVendorCatalogItems(userId);

  const { data: remainingItemToUpload } = useGetRemainingItemToUpload(userId);
  const isItemLimitReached =
    remainingItemToUpload?.count !== undefined
      ? remainingItemToUpload.count <= 0
      : false;

  /**
   * This function is called when the user clicks the "Add Items" or "Sell Now" button
   * It should open the modal for the vendor to add items
   */
  const onAddItemsClick = () => {
    setIsAddItemsOpened(true);
  };

  return (
    <Page.Container>
      <Header.Major icon="star" title="Sell To Us">
        {isItemLimitReached ? (
          <Tooltip
            content="You have reached the upload limit for now. Please try again later."
            position="left"
            arrow
            zIndex={10}
          >
            <Button
              data-testid="add-items-button"
              onClick={onAddItemsClick}
              disabled={isItemLimitReached}
            >
              Add Items
            </Button>
          </Tooltip>
        ) : (
          <Button
            data-testid="add-items-button"
            onClick={onAddItemsClick}
            disabled={isItemLimitReached}
          >
            Add Items
          </Button>
        )}
      </Header.Major>
      <Header.Minor
        search={{
          value: paramFilters.search || '',
          onChange: debounce(
            (value) => setParamFilter('search', value || undefined),
            500,
          ),
        }}
      >
        <Tabs.Group type="segment">
          <Clickable
            onClick={() => setParamFilter('stage', VPStages.PENDING_FN)}
          >
            <Tabs.Item
              isActive={paramFilters.stage === VPStages.PENDING_FN}
              type="segment"
              countBadge={
                (count?.[ItemFilters.NEEDS_REVIEW] || 0) +
                (count?.[ItemFilters.ACCEPTED] || 0)
              }
            >
              {VPStages.PENDING_FN}
            </Tabs.Item>
          </Clickable>
          <Clickable
            onClick={() => setParamFilter('stage', VPStages.NEEDS_DETAILS)}
          >
            <Tabs.Item
              isActive={paramFilters.stage === VPStages.NEEDS_DETAILS}
              type="segment"
              countBadge={count?.[ItemFilters.PENDING_VENDOR] || 0}
            >
              {VPStages.NEEDS_DETAILS}
            </Tabs.Item>
          </Clickable>
          <Clickable
            onClick={() => setParamFilter('stage', VPStages.DETAILS_SENT)}
          >
            <Tabs.Item
              isActive={paramFilters.stage === VPStages.DETAILS_SENT}
              type="segment"
              countBadge={
                (count?.[ItemFilters.PO_DRAFTS] || 0) +
                (count?.[ItemFilters.FINAL_REVIEW] || 0) +
                (count?.[ItemFilters.MANAGER_REVIEW] || 0)
              }
            >
              {VPStages.DETAILS_SENT}
            </Tabs.Item>
          </Clickable>
          <Clickable
            onClick={() => setParamFilter('stage', VPStages.PO_CREATED)}
          >
            <Tabs.Item
              isActive={paramFilters.stage === VPStages.PO_CREATED}
              type="segment"
              countBadge={count?.[ItemFilters.PO_CREATED] || 0}
            >
              {VPStages.PO_CREATED}
            </Tabs.Item>
          </Clickable>
          <Clickable onClick={() => setParamFilter('stage', VPStages.PASSED)}>
            <Tabs.Item
              isActive={paramFilters.stage === VPStages.PASSED}
              type="segment"
              countBadge={count?.[ItemFilters.REJECTED] || 0}
            >
              {VPStages.PASSED}
            </Tabs.Item>
          </Clickable>
          <Clickable onClick={() => setParamFilter('stage', undefined)}>
            <Tabs.Item
              isActive={!paramFilters.stage}
              type="segment"
              countBadge={
                (count?.[ItemFilters.NEEDS_REVIEW] || 0) +
                (count?.[ItemFilters.ACCEPTED] || 0) +
                (count?.[ItemFilters.PENDING_VENDOR] || 0) +
                (count?.[ItemFilters.PO_DRAFTS] || 0) +
                (count?.[ItemFilters.FINAL_REVIEW] || 0) +
                (count?.[ItemFilters.MANAGER_REVIEW] || 0) +
                (count?.[ItemFilters.PO_CREATED] || 0) +
                (count?.[ItemFilters.REJECTED] || 0)
              }
            >
              All
            </Tabs.Item>
          </Clickable>
        </Tabs.Group>
      </Header.Minor>
      <CategoryClassFilter />
      {!isLoading && items.length === 0 ? (
        <EmptyList onAddItemsClick={onAddItemsClick} />
      ) : (
        <ItemCardList
          items={items}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
        />
      )}
      <AddItemsModal
        userId={userId}
        isAddItemsOpened={isAddItemsOpened}
        setIsAddItemsOpened={setIsAddItemsOpened}
      />
    </Page.Container>
  );
}

export default SellToUs;
